import { BOSSES_DATA } from "./bosses.data.js";
import { LOOT } from "./loot.js";
import { buildNpcCatalog, NPC_STAT_FIELDS, NPCS } from "./npcs.js";
import type { NpcDef } from "./npcs.js";

export interface PounceParams {
  bitesBeforeLeap: number;
  windupTicks: number;
  flightTicks: number;
  recoveryTicks: number;
  leapRange: number;
  impactRadius: number;
  damagePct: number;
}
const POUNCE_FIELDS = [
  { key: "bitesBeforeLeap", label: "Bites before pounce", min: 1, unit: "integer" },
  { key: "windupTicks", label: "Crouch / warning duration", min: 1, unit: "ticks" },
  { key: "flightTicks", label: "Flight duration", min: 1, unit: "ticks" },
  { key: "recoveryTicks", label: "Landing recovery", min: 1, unit: "ticks" },
  { key: "leapRange", label: "Maximum leap distance", min: 1, unit: "tiles" },
  { key: "impactRadius", label: "Landing damage radius", min: 1, unit: "tiles" },
  { key: "damagePct", label: "Landing damage multiplier", min: 0, unit: "percent" },
] as const;
/** Behaviour is code; the parameters and boss identities are human-authored data. */
export type BossMechanic = { kind: "none" } | {
  kind: "orb-burst";
  minOrbs: number;
  maxOrbs: number;
  countdownTicks: number;
  baseWindowTicks: number;
  perOrbTicks: number;
  firstDamagePct: number;
  secondDamagePct: number;
  thirdDamagePct: number;
  fourthDamagePct: number;
} | {
  kind: "heavy-freeze";
  chanceNumerator: number;
  chanceDenominator: number;
  attackDurationPct: number;
  damagePct: number;
  freezeTicks: number;
} | (PounceParams & { kind: "pounce" }) | (PounceParams & {
  kind: "fire-pounce";
  fireTicks: number;
  fireIntervalTicks: number;
  fireDamagePct: number;
}) | {
  kind: "meteor-shower";
  intervalTicks: number;
  meteorCount: number;
  windupTicks: number;
  recoveryTicks: number;
  damagePct: number;
} | {
  kind: "freeze-blink";
  averageIntervalTicks: number;
  windupTicks: number;
  freezeTicks: number;
  minDistance: number;
  tauntTicks: number;
};
export const BOSS_MECHANIC_KINDS = ["none", "heavy-freeze", "pounce", "fire-pounce", "freeze-blink", "meteor-shower", "orb-burst"] as const;
export const BOSS_MECHANIC_FIELDS = {
  "orb-burst": [
    { key: "minOrbs", label: "Minimum orbs", min: 1, unit: "integer" },
    { key: "maxOrbs", label: "Maximum orbs (up to 5)", min: 1, unit: "integer" },
    { key: "countdownTicks", label: "Wave countdown", min: 1, unit: "ticks" },
    { key: "baseWindowTicks", label: "Initial reaction allowance", min: 0, unit: "ticks" },
    { key: "perOrbTicks", label: "Additional window per orb", min: 1, unit: "ticks" },
    { key: "firstDamagePct", label: "First click (% of maximum HP)", min: 1, unit: "percent" },
    { key: "secondDamagePct", label: "Second click (% of maximum HP)", min: 1, unit: "percent" },
    { key: "thirdDamagePct", label: "Third click (% of maximum HP)", min: 1, unit: "percent" },
    { key: "fourthDamagePct", label: "Fourth click (% of maximum HP)", min: 1, unit: "percent" },
  ],
  "heavy-freeze": [
  { key: "chanceNumerator", label: "Special chance numerator", min: 0, unit: "integer" },
  { key: "chanceDenominator", label: "Special chance denominator", min: 1, unit: "integer" },
  { key: "attackDurationPct", label: "Attack duration", min: 1, unit: "percent" },
  { key: "damagePct", label: "Damage multiplier", min: 0, unit: "percent" },
  { key: "freezeTicks", label: "Freeze duration", min: 1, unit: "ticks" },
  ],
  pounce: POUNCE_FIELDS,
  "fire-pounce": [
    ...POUNCE_FIELDS,
    { key: "fireTicks", label: "Ground fire duration", min: 1, unit: "ticks" },
    { key: "fireIntervalTicks", label: "Ground fire pulse interval", min: 1, unit: "ticks" },
    { key: "fireDamagePct", label: "Ground fire damage multiplier", min: 0, unit: "percent" },
  ],
  "freeze-blink": [
    { key: "averageIntervalTicks", label: "Average escape interval", min: 1, unit: "ticks" },
    { key: "windupTicks", label: "Escape warning duration", min: 1, unit: "ticks" },
    { key: "freezeTicks", label: "Freeze duration", min: 1, unit: "ticks" },
    { key: "minDistance", label: "Minimum escape separation", min: 1, unit: "tiles" },
    { key: "tauntTicks", label: "Speech bubble duration", min: 1, unit: "ticks" },
  ],
  "meteor-shower": [
    { key: "intervalTicks", label: "Time between meteor volleys", min: 1, unit: "ticks" },
    { key: "meteorCount", label: "Meteor count", min: 1, unit: "integer" },
    { key: "windupTicks", label: "Shadow warning duration", min: 1, unit: "ticks" },
    { key: "recoveryTicks", label: "Meteor recovery duration", min: 1, unit: "ticks" },
    { key: "damagePct", label: "Meteor damage multiplier", min: 0, unit: "percent" },
  ],
} as const;

export function defaultBossMechanic(kind: BossMechanic["kind"]): BossMechanic {
  switch (kind) {
    case "none": return { kind };
    case "orb-burst": return { kind, minOrbs: 3, maxOrbs: 5, countdownTicks: 60, baseWindowTicks: 10, perOrbTicks: 8,
      firstDamagePct: 1, secondDamagePct: 3, thirdDamagePct: 10, fourthDamagePct: 25 };
    case "heavy-freeze": return { kind, chanceNumerator: 1, chanceDenominator: 3, attackDurationPct: 200, damagePct: 200, freezeTicks: 200 };
    case "pounce": return { kind, bitesBeforeLeap: 3, windupTicks: 20, flightTicks: 10, recoveryTicks: 12, leapRange: 5, impactRadius: 1, damagePct: 125 };
    case "fire-pounce": return { ...defaultBossMechanic("pounce") as PounceParams, kind, fireTicks: 80, fireIntervalTicks: 20, fireDamagePct: 25 };
    case "freeze-blink": return { kind, averageIntervalTicks: 160, windupTicks: 10, freezeTicks: 40, minDistance: 3, tauntTicks: 80 };
    case "meteor-shower": return { kind, intervalTicks: 160, meteorCount: 8, windupTicks: 30, recoveryTicks: 12, damagePct: 200 };
  }
}
export type BossDef = Omit<NpcDef, "kind"> & { id: number; mechanic: BossMechanic; taunts?: readonly string[] };

/** Reserved internal namespace: an ordinary NPC kind cannot contain a colon. */
export function bossKind(id: number): string { return `boss:${id}`; }
/** Adapt a boss to the SAME combat/art substrate; authoring remains separate. */
export function bossAsNpc(boss: BossDef): NpcDef {
  const { id, mechanic: _mechanic, taunts: _taunts, ...npc } = boss;
  void _mechanic;
  void _taunts;
  return { ...npc, kind: bossKind(id) };
}

export function buildBossCatalog(records: readonly unknown[], lootIds?: ReadonlySet<number>): readonly BossDef[] {
  const seen = new Set<number>();
  return records.map((record) => {
    if (!record || typeof record !== "object") throw new Error("boss entry must be an object");
    const raw = record as Record<string, unknown>;
    const id = raw.id;
    if (typeof id !== "number" || !Number.isSafeInteger(id) || id < 0) throw new Error("boss id must be a non-negative integer");
    if (seen.has(id)) throw new Error(`duplicate boss id ${id}`);
    seen.add(id);
    const npc = buildNpcCatalog([{ ...raw, kind: bossKind(id) }])[0]!;
    if (lootIds && npc.lootTable !== null && !lootIds.has(npc.lootTable)) throw new Error(`boss ${id}: unknown loot table ${npc.lootTable}`);
    for (const key of Object.keys(npc.stats ?? {})) {
      const field = NPC_STAT_FIELDS.find((f) => f.key === key);
      if (!field) throw new Error(`boss ${id}: unknown stat ${key}`);
      if (npc.stats![key]! < field.min) throw new Error(`boss ${id}: ${key} must be >= ${field.min}`);
    }
    if ((npc.stats?.atkMin ?? 0) > (npc.stats?.atkMax ?? npc.stats?.atkMin ?? 0)) throw new Error(`boss ${id}: minimum damage exceeds maximum`);
    const input = raw.mechanic;
    if (!input || typeof input !== "object") throw new Error(`boss ${id}: missing mechanic`);
    const m = input as Record<string, unknown>;
    let mechanic: BossMechanic;
    if (m.kind === "none") {
      if (Object.keys(m).some((k) => k !== "kind")) throw new Error(`boss ${id}: none has no parameters`);
      mechanic = { kind: "none" };
    } else if (m.kind === "heavy-freeze" || m.kind === "pounce" || m.kind === "fire-pounce" || m.kind === "freeze-blink" || m.kind === "meteor-shower" || m.kind === "orb-burst") {
      const fields = BOSS_MECHANIC_FIELDS[m.kind];
      for (const field of fields) {
        const value = m[field.key];
        if (typeof value !== "number" || !Number.isSafeInteger(value) || value < field.min) throw new Error(`boss ${id}: ${field.key} must be an integer >= ${field.min}`);
      }
      if (Object.keys(m).some((k) => k !== "kind" && !fields.some((f) => f.key === k))) throw new Error(`boss ${id}: unknown mechanic parameter`);
      if (m.kind === "orb-burst") {
        if ((m.minOrbs as number) > (m.maxOrbs as number) || (m.maxOrbs as number) > 5) throw new Error(`boss ${id}: orb counts must satisfy 1 <= minimum <= maximum <= 5`);
        if ((m.countdownTicks as number) > 12000 || (m.baseWindowTicks as number) > 12000 || (m.perOrbTicks as number) > 12000) throw new Error(`boss ${id}: orb durations must not exceed 12000 ticks`);
        const damage = [m.firstDamagePct, m.secondDamagePct, m.thirdDamagePct, m.fourthDamagePct] as number[];
        if (damage.some((v, i) => v > 100 || (i > 0 && v < damage[i - 1]!))) throw new Error(`boss ${id}: orb damage must increase from 1 to 100 percent`);
        mechanic = { kind: "orb-burst", minOrbs: m.minOrbs as number, maxOrbs: m.maxOrbs as number,
          countdownTicks: m.countdownTicks as number, baseWindowTicks: m.baseWindowTicks as number, perOrbTicks: m.perOrbTicks as number,
          firstDamagePct: damage[0]!, secondDamagePct: damage[1]!, thirdDamagePct: damage[2]!, fourthDamagePct: damage[3]! };
      } else if (m.kind === "meteor-shower") {
        mechanic = { kind: "meteor-shower", intervalTicks: m.intervalTicks as number, meteorCount: m.meteorCount as number,
          windupTicks: m.windupTicks as number, recoveryTicks: m.recoveryTicks as number, damagePct: m.damagePct as number };
      } else if (m.kind === "freeze-blink") {
        if ((m.averageIntervalTicks as number) > 0xffffffff) throw new Error(`boss ${id}: average escape interval exceeds RNG range`);
        mechanic = { kind: "freeze-blink", averageIntervalTicks: m.averageIntervalTicks as number,
          windupTicks: m.windupTicks as number, freezeTicks: m.freezeTicks as number,
          minDistance: m.minDistance as number, tauntTicks: m.tauntTicks as number };
      } else if (m.kind === "pounce" || m.kind === "fire-pounce") {
        const pounce: PounceParams = { bitesBeforeLeap: m.bitesBeforeLeap as number,
          windupTicks: m.windupTicks as number, flightTicks: m.flightTicks as number,
          recoveryTicks: m.recoveryTicks as number, leapRange: m.leapRange as number,
          impactRadius: m.impactRadius as number, damagePct: m.damagePct as number };
        if (m.kind === "fire-pounce" && (m.fireIntervalTicks as number) >= (m.fireTicks as number)) throw new Error(`boss ${id}: fire pulse interval must be shorter than fire duration`);
        mechanic = m.kind === "pounce" ? { kind: "pounce", ...pounce } : { kind: "fire-pounce", ...pounce,
          fireTicks: m.fireTicks as number, fireIntervalTicks: m.fireIntervalTicks as number, fireDamagePct: m.fireDamagePct as number };
      } else {
        const chanceNumerator = m.chanceNumerator as number;
        const chanceDenominator = m.chanceDenominator as number;
        if (chanceNumerator > chanceDenominator) throw new Error(`boss ${id}: chance numerator exceeds denominator`);
        mechanic = { kind: "heavy-freeze", chanceNumerator, chanceDenominator,
          attackDurationPct: m.attackDurationPct as number, damagePct: m.damagePct as number, freezeTicks: m.freezeTicks as number };
      }
    } else throw new Error(`boss ${id}: unknown mechanic ${String(m.kind)}`);
    const { kind: _kind, ...fields } = npc;
    void _kind;
    const taunts = raw.taunts;
    if (taunts !== undefined && (!Array.isArray(taunts) || taunts.length > 32 ||
        taunts.some((line: unknown) => typeof line !== "string" || !line.trim() || line.length > 200 || /[\r\n\x00-\x1f]/.test(line)))) {
      throw new Error(`boss ${id}: taunts must be at most 32 non-empty single-line strings (200 characters each)`);
    }
    return { id, ...fields, mechanic, ...(taunts === undefined ? {} : { taunts: (taunts as string[]).map((line) => line.trim()) }) };
  });
}
export const BOSSES = buildBossCatalog(BOSSES_DATA, new Set(LOOT.map((t) => t.id)));
export function bossById(id: number): BossDef | undefined { return BOSSES.find((b) => b.id === id); }

export type BossParseResult = { ok: true; bosses: BossDef[]; nextId: number; errors: string[] } |
  { ok: false; bosses: BossDef[]; nextId: number; errors: string[] };

/** Pure shared parser: tools, shipping loaders and fixtures use one validator. */
export function parseBossStore(text: string, lootIds?: ReadonlySet<number>): BossParseResult {
  const records: Record<string, unknown>[] = [];
  const errors: string[] = [];
  let current: Record<string, unknown> | null = null;
  let section: "fields" | "stats" | "mechanicParams" = "fields";
  let nextId = Number(/^# next_id ; (\d+)$/m.exec(text)?.[1] ?? 0);
  const fields = ["name", "family", "shade", "style", "stackable", "movable", "loot", "spellColor", "spellRadius", "mechanic", "taunts"];
  for (const [i, raw] of text.split(/\r?\n/).entries()) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const header = /^\[boss\]\s+(\d+)$/.exec(line);
    if (header) {
      current = { id: Number(header[1]), shade: null, tint: "canon", style: "melee", stats: {}, mechanic: { kind: "none" } };
      records.push(current);
      nextId = Math.max(nextId, Number(header[1]) + 1);
      section = "fields";
      continue;
    }
    const fail = (message: string): void => { errors.push(`line ${i + 1}: ${message}`); };
    if (!current) { fail("field outside a [boss] block"); continue; }
    if (line === "stats" || line === "mechanicParams") { section = line; continue; }
    const kv = /^([a-zA-Z][a-zA-Z0-9]*)\s*;\s*(.*)$/.exec(line);
    if (!kv) { fail("expected key ; value"); continue; }
    const key = kv[1]!;
    const value = kv[2]!.trim();
    if (section !== "fields") {
      const supported = section === "stats" ? NPC_STAT_FIELDS.some((f) => f.key === key) : Object.values(BOSS_MECHANIC_FIELDS).some((fields) => fields.some((f) => f.key === key));
      if (!supported || !/^\d+$/.test(value)) { fail(`invalid ${section} field ${key}`); continue; }
      (current[section === "stats" ? "stats" : "mechanic"] as Record<string, unknown>)[key] = Number(value);
    } else if (!fields.includes(key)) fail(`unknown field ${key}`);
    else if (key === "mechanic") current.mechanic = { kind: value };
    else if (key === "taunts") {
      try { current.taunts = JSON.parse(value) as unknown; }
      catch { fail("taunts must be a JSON array of strings"); }
    }
    else if (key === "shade") {
      const shade = value ? "#" + value.replace(/^#/, "").toLowerCase() : null;
      current.shade = shade;
      current.tint = shade ? shade.slice(1) : "canon";
    } else if (key === "loot" || key === "spellRadius") {
      if (value && !/^\d+$/.test(value)) fail(`${key} must be an integer`);
      current[key === "loot" ? "lootTable" : key] = value ? Number(value) : null;
    } else if (key === "stackable" || key === "movable") {
      if (value !== "true" && value !== "false") fail(`${key} must be true or false`);
      else current[key] = value === "true";
    } else if (key === "spellColor") { if (value) current[key] = value; }
    else current[key] = value;
  }
  let bosses: BossDef[] = [];
  try { bosses = [...buildBossCatalog(records, lootIds)]; } catch (error) { errors.push(error instanceof Error ? error.message : String(error)); }
  return { ok: errors.length === 0, bosses, nextId, errors };
}

export function serializeBossStore(bosses: readonly BossDef[], nextId = 0): string {
  const out = ["# bosses.store — boss definitions; edit in the knobs app's Bosses tab.",
    "# Permanent IDs, never reused. Floors reference these IDs, ordinary rooms use npc.store.",
    "# Mechanics are implemented in sim; their parameters are data. Durations are ticks.",
    `# next_id ; ${Math.max(nextId, ...bosses.map((b) => b.id + 1))}`, ""];
  for (const b of bosses) {
    out.push(`[boss] ${b.id}`, `name ; ${b.name}`, `family ; ${b.family}`, `shade ; ${b.shade ?? ""}`,
      `style ; ${b.style ?? "melee"}`, `stackable ; ${b.stackable ?? false}`, `movable ; ${b.movable ?? true}`, `loot ; ${b.lootTable ?? ""}`);
    if (b.spellColor) out.push(`spellColor ; ${b.spellColor}`);
    if (b.spellRadius !== undefined) out.push(`spellRadius ; ${b.spellRadius}`);
    if (b.taunts !== undefined) out.push(`taunts ; ${JSON.stringify(b.taunts)}`);
    out.push(`mechanic ; ${b.mechanic.kind}`, "stats");
    for (const f of NPC_STAT_FIELDS) if (b.stats?.[f.key] !== undefined) out.push(`  ${f.key} ; ${b.stats[f.key]}`);
    if (b.mechanic.kind !== "none") {
      out.push("mechanicParams");
      for (const f of BOSS_MECHANIC_FIELDS[b.mechanic.kind]) out.push(`  ${f.key} ; ${(b.mechanic as unknown as Record<string, number>)[f.key]}`);
    }
    out.push("");
  }
  return out.join("\n");
}

/** Display/combat adapters, never the NPC authoring catalog. */
export const ENEMY_DEFS: readonly NpcDef[] = [...NPCS, ...BOSSES.map(bossAsNpc)];
export function enemyByKind(kind: string): NpcDef | undefined { return ENEMY_DEFS.find((d) => d.kind === kind); }
